import axios from 'axios';
import dayjs from 'dayjs';

import { TablePagination, TextField } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

import React, { useEffect, useState } from 'react';

import { toast } from 'react-toastify';

import AddCertItems from '@src/components/modals/addCertificateItems';
import EditCertItems from '@src/components/modals/editCertificateItems';
import { useAuth } from '@src/contexts/authContext';
import { formatDate } from '@src/utils';

const url = `${process.env.REACT_APP_API_URL}/admin/v3/financial-reports/branchs`;

export default function ViewSentReports() {
  const { auth } = useAuth();

  const [loading, setLoading] = useState();
  const [item, setItem] = useState([]);

  const fetchData = (values = {}) => {
    setLoading(true);

    const { date } = values;

    if (!date) {
      console.warn('Date is required to fetch data');
      setLoading(false);
      return;
    }

    const month = dayjs(date).format('MM');
    const year = dayjs(date).format('YYYY');

    const params = new URLSearchParams({
      month,
      year,
      page: 0,
      limit: item.length || 1000,
    }).toString();

    axios
      .get(`${url}/histories?${params}`, {
        headers: {
          Authorization: `Bearer ${auth.idToken}`,
        },
      })
      .then((response) => {
        setItem(response.data?.response?.data || []);
      })
      .catch((error) => {
        console.error('Failed to fetch data:', error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  function downloadReport(id) {
    const params = new URLSearchParams({
      history_id: id,
    });

    axios
      .get(`${url}/download-by-history?${params}`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth.idToken}`,
        },
        responseType: 'blob',
      })
      .then((response) => {
        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });

        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;

        const contentDisposition = response.headers['content-disposition'];
        let fileName = 'report.xlsx';
        if (contentDisposition && contentDisposition.includes('filename=')) {
          fileName = contentDisposition
            .split('filename=')[1]
            .split(';')[0]
            .replace(/"/g, '');
        }

        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);

        toast.success('Downloaded successfully', {
          autoClose: 2000,
          position: 'top-center',
        });
      })
      .catch((error) => {
        const message =
          error.response?.data?.system?.message || 'Download failed';

        toast.error(`${message}`, {
          autoClose: 2000,
          position: 'top-center',
        });
      });
  }

  const [date, setDate] = useState(dayjs());

  const handleStartDate = (newValue) => {
    const newDate = newValue ? dayjs(newValue) : null;
    setDate(newDate);

    if (newDate) {
      fetchData({ date: newDate });
    }
  };

  useEffect(() => {
    if (date) {
      fetchData({ date });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const [openModal, setOpenModal] = useState(false);
  const showModal = () => {
    setOpenModal(true);
  };

  const [openModals, setOpenModals] = useState(false);
  const showModals = (itemId) => {
    setOpenModals(itemId);
  };

  const [searchQuery, setSearchQuery] = useState('');
  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
  };
  const filteredItems = item.filter(
    (row) =>
      row?.clinic?.clinicName
        .toLowerCase()
        .includes(searchQuery.toLowerCase()) ||
      row?.email_receiver.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  return (
    <div>
      <div className='py-4 pl-5'>
        <div className='flex justify-between'>
          {/* Search Box */}
          <Box sx={{ width: '100%' }} className='justify-end px-3 py-4'>
            <TextField
              fullWidth
              size='medium'
              variant='outlined'
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: 'white',
                  height: '50px',
                },
                mt: '5px',
                width: '250px',
              }}
              onChange={handleSearch}
              placeholder='Search by name or email'
            />
          </Box>

          {/* Calendar */}
          <Box sx={{ width: '100%' }} className='flex justify-end py-4'>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                value={date}
                onChange={handleStartDate}
                views={['year', 'month']}
                inputFormat='MM YYYY'
                reduceAnimations
                sx={{ width: '250px' }}
                slotProps={{
                  textField: {
                    variant: 'outlined',
                    InputProps: {
                      sx: {
                        height: '50px',
                        backgroundColor: 'white',
                        fontSize: '14px',
                        padding: '0 8px',
                        borderRadius: '12px',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#D1D5DB',
                        },
                      },
                    },
                    inputProps: {
                      style: {
                        padding: '0px 6px',
                      },
                    },
                  },
                }}
              />
            </LocalizationProvider>
          </Box>
        </div>

        {loading ? (
          <div className='font-poppins py-6 px-8 text-gray-500'>Loading...</div>
        ) : (
          <span className='flex flex-col items-start py-4 '>
            <TableContainer
              className='rounded-xl'
              component={Paper}
              style={{
                width: '100%',
                paddingBottom: '12px',
                paddingRight: '12px',
                backgroundColor: '#ffffff',
                borderColor: 'ffffff',
              }}
            >
              <Table
                sx={{
                  minWidth: 350,
                  backgroundColor: '#ffffff',
                  borderColor: 'ffffff',
                }}
                aria-label='simple table'
              >
                <TableHead>
                  <TableRow>
                    <TableCell
                      className='text-xs font-bold text-gray-400'
                      style={{
                        width: '250px',
                        border: 'gray',
                        paddingLeft: 30,
                      }}
                    >
                      Clinic Name
                    </TableCell>
                    <TableCell
                      className='text-xs font-bold text-gray-400'
                      style={{ padding: 4, width: '100px', border: 'gray' }}
                    >
                      Email Receiver
                    </TableCell>

                    <TableCell
                      className='text-xs font-bold text-gray-400'
                      style={{ padding: 4, width: '150px', border: 'gray' }}
                    >
                      Sent at
                    </TableCell>

                    <TableCell
                      className='text-xs font-bold text-gray-400'
                      style={{ padding: 4, width: '100px', border: 'gray' }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Array.isArray(filteredItems) && filteredItems.length > 0 ? (
                    filteredItems
                      .slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage,
                      )
                      .map((row) => (
                        <TableRow
                          key={row?._id}
                          hover
                          tabIndex={-1}
                          sx={{
                            '&:last-child td, &:last-child th': { border: 0 },
                          }}
                        >
                          <TableCell
                            component='th'
                            scope='row'
                            style={{
                              margin: 2,
                              paddingLeft: 30,
                              maxWidth: 100,
                            }}
                            className='border-white text-sm font-bold text-gray-500'
                          >
                            {row?.clinic?.clinicName || '-'}
                          </TableCell>

                          <TableCell
                            className='border-white text-sm font-bold text-gray-500'
                            style={{
                              padding: 0,
                              maxWidth: 200,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                            title={row?.email_receiver}
                          >
                            {row?.email_receiver}
                          </TableCell>
                          <TableCell
                            className='border-white text-sm font-bold text-gray-500'
                            style={{ padding: 4, margin: 2 }}
                          >
                            {row?.sent_at &&
                            !Number.isNaN(new Date(row.sent_at).getTime())
                              ? formatDate(row.sent_at)
                              : '-'}
                          </TableCell>

                          <TableCell
                            className='border-white'
                            style={{ padding: 4, margin: 2 }}
                          >
                            <div className='justify-start '>
                              <Button
                                variant='contained'
                                className='text-xs font-bold text-purple-600'
                                sx={{
                                  textTransform: 'none',
                                  backgroundColor: '#FAF9FC',
                                  '&:hover': {
                                    backgroundColor: 'white',
                                  },
                                }}
                                onClick={() => downloadReport(row?._id)}
                              >
                                Download
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={4}
                        className='text-center text-gray-500'
                      >
                        No data available
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <Box
                className='flex justify-end py-3'
                sx={{ width: '100%', fontFamily: 'Poppins' }}
              >
                <TablePagination
                  rowsPerPageOptions={[10, 50, 100]}
                  component='div'
                  count={item.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </Box>
            </TableContainer>
          </span>
        )}
      </div>

      <AddCertItems
        setOpenModal={setOpenModal}
        showModal={showModal}
        openModal={openModal}
        fetchData={fetchData}
      />

      <EditCertItems
        setOpenModals={setOpenModals}
        showModals={showModals}
        openModals={openModals}
        fetchData={fetchData}
      />
      <br />
      <br />
      <br />
    </div>
  );
}
